/**
 * @module app.shared
 */

import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { isValid } from 'date-fns';
import { format } from 'date-fns-tz';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { DateLocaleService } from 'app/shared/services/date-locale-service';
import { IFormatter, IFormatOptions, IDateTimeFormatOptions } from 'app/shared/lib/formatting/formatting-types';
import { getResourceFormat, getLanguageLocale } from 'app/shared/lib/formatting/formatting-utilities';
import { DateTimeValue, DateValue } from 'app/shared/lib/date-time';
import { mapTimeZone, changeLocalTimeZone } from 'app/shared/lib/date-time-core';

export abstract class BaseDateTimeFormatter implements IFormatter {

    protected readonly resourceKeyPrefix: string = 'ui:formats.dateTime';
    protected readonly dateLocaleKey: string = 'ui:locales.date';

    constructor(protected readonly env: EnvironmentService, protected readonly i18n: I18N, protected readonly dateLocales: DateLocaleService) { }

    public formatValue(value: unknown, options?: IFormatOptions): any {
        if(value === null || value === undefined) { return null; }
        const dateTimeOptions = options as IDateTimeFormatOptions;

        let dateUtc: Date;
        if(value instanceof Date) {
            dateUtc = value;
        }
        else if(value instanceof DateValue) {
            dateUtc = this.processDateValue(value);
        }
        else if(value instanceof DateTimeValue) {
            dateUtc = this.processDateTimeValue(value);
        }
        else if(typeof value === 'string') {
            dateUtc = this.processStringValue(value);
        }
        else {
            return null;
        }

        if(!isValid(dateUtc)) { return null; }

        // MW: We're simulating an i18n translation context format by suffixing the "format" with the context.
        if(options?.format && dateTimeOptions.time24) { options.format += '_24' }

        const formatString = getResourceFormat(this.i18n, this.resourceKeyPrefix, options);
        const localeCode = getLanguageLocale(this.i18n, this.dateLocaleKey, options);
        const locale = this.dateLocales.getLocale(localeCode) || undefined;

        const timeZone = (dateTimeOptions && dateTimeOptions.timeZone) || this.getDisplayTimeZone();
        const displayDate = this.calculateDisplayDate(dateUtc, timeZone);

        return format(displayDate, formatString, { locale, timeZone });

    }

    protected processDateTimeValue(dateTime: DateTimeValue): Date {
        return DateTimeValue.toNativeDate(dateTime);
    }

    protected processDateValue(date: DateValue): Date {
        return DateValue.toNativeDate(date, this.getReferenceTimeZone());
    }

    protected processStringValue(dateTime: string): Date {
        const dateTimeValue = DateTimeValue.parseIsoDateTime(dateTime, this.getReferenceTimeZone() || undefined);
        return DateTimeValue.toNativeDate(dateTimeValue);
    }

    protected getReferenceTimeZone(): string {
        return this.env.timeZone;
    }

    protected getDisplayTimeZone(): string {
        return this.env.displayTimeZone;
    }

    protected calculateDisplayDate(dateUtc: Date, timeZone: string | null): Date {
        if(timeZone) {
            return changeLocalTimeZone(dateUtc, mapTimeZone(timeZone));
        }
        return dateUtc;
    }

}

@autoinject
export class TimestampFormatter extends BaseDateTimeFormatter implements IFormatter {

    public readonly resourceKeyPrefix: string = 'ui:formats.timestamp';

    constructor(env: EnvironmentService, i18n: I18N, dateLocales: DateLocaleService) {
        super(env, i18n, dateLocales);
    }

}

@autoinject
export class DateTimeFormatter extends BaseDateTimeFormatter implements IFormatter {

    public readonly resourceKeyPrefix: string = 'ui:formats.dateTime';

    constructor(env: EnvironmentService, i18n: I18N, dateLocales: DateLocaleService) {
        super(env, i18n, dateLocales);
    }

}

@autoinject
export class TimeFormatter extends BaseDateTimeFormatter implements IFormatter {

    public readonly resourceKeyPrefix: string = 'ui:formats.time';

    constructor(env: EnvironmentService, i18n: I18N, dateLocales: DateLocaleService) {
        super(env, i18n, dateLocales);
    }

}

@autoinject
export class DateFormatter extends BaseDateTimeFormatter implements IFormatter {

    public readonly resourceKeyPrefix: string = 'ui:formats.date';

    constructor(env: EnvironmentService, i18n: I18N, dateLocales: DateLocaleService) {
        super(env, i18n, dateLocales);
    }

    // NOTE: The Date-only formatter is special because dates do not have time values and
    // are implied to have a time of midnight in the tenant's time zone. Since JavaScript
    // Date values always have a time and an implied time zone, this gets tricky.
    // --Matthew W. Jackson

    protected processStringValue(date: string): Date {
        if(date && date.length === 10) {
            // a string value will be yyyy-MM-dd
            // parse it using DateValue
            const dateValue = new DateValue(date);
            // convert it to UTC with tenant's reference time zone
            const result = DateValue.toNativeDate(dateValue, this.getReferenceTimeZone());
            return result;
        }
        // fall back on parsing as a date/time ISO string
        return super.processStringValue(date);
    }

    protected getDisplayTimeZone(): string {
        // NOTE: since "Date" values have no time information, they don't really have a time
        // zone. However, we convert them to a JavaScript Date for formatting, using the tenant's
        // time zone to determine midnight. Therefore, we must use this same time zone as a display
        // time zone (not the user's time zone) in order to get the same value out as we put in.
        // [mwj]
        return this.env.timeZone;
    }

}

